import { useEffect, useMemo, useState } from "react";
import Axios from "axios";
import { toast } from "react-toastify";
import LoaderImg from "@assets/images/loader.gif";
import ReactTableTrademo from "@shared/trademo-datatable/react-table-trademo";
import { getTitleCase, useDebounce } from "@utils/utils";
import NoSearchDataComponent from "@shared/country-components/no-search-data";
import RequestCenterFilter from "./Request-center-filter";
import { getApiUrl } from "@utils/baseUrl";
import { Search } from "react-feather";
import trackingEventDictionary from "@core/dictionary/tracking-event";
import { trademoTrackingEvents } from "@core/global/trademo-tracking";
import { generateTrackingObject } from "@core/global/global-country-functions";
import { useLocation } from "react-router-dom";
import { getUserActivePlan } from "@helpers/profileHelper";
import { captureException } from "@sentry/react";

const statuses = {
  pending: {
    key: "pending",
    icon: "rotate_right",
    sideIcon: (
      <svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="20px" fill="#fdb022">
        <path d="M516-73v-53q41-6 79-24t70-49l37.15 36.38q-38.15 38.93-86.38 60.89T516-73Zm-72 0Q323.85-85.85 243.92-174.77 164-263.69 164-387q0-65.77 24.77-123.11 24.77-57.35 67.77-100.35 43-43 100.35-67.27Q414.23-702 480-702h51.31l-100.77-99.77 37.15-36.38L623.84-682 467.69-526.85 430.54-564l87-86H479q-110 0-186.5 76.5T216-387q0 101 64 174t164 87.77V-73Zm303.38-144.85L708-257q19-33 27.19-68.81 8.19-35.81 8.58-73.19H796q0 47.15-11.16 93.38-11.15 46.23-37.46 87.77ZM785-471h-55.23q-9.39-28.38-24.58-54.19Q690-551 667-574l36.38-36.15q29.93 29.54 50.27 64.88Q774-509.92 785-471Z" />
      </svg>
    ),
    bgColor: "",
  },
  delivered: {
    key: "delivered",
    icon: "check_circle",
    sideIcon: (
      <svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="20px" fill="#32d583">
        <path d="m428.23-349.85 224.92-223.92L616-610.92 428.23-424.15l-85-84L306.08-471l122.15 121.15ZM480.07-116q-74.84 0-141.21-28.42t-116.18-78.21q-49.81-49.79-78.25-116.13Q116-405.1 116-479.93q0-75.84 28.42-141.71t78.21-115.68q49.79-49.81 116.13-78.25Q405.1-844 479.93-844q75.84 0 141.71 28.42t115.68 78.21q49.81 49.79 78.25 115.63Q844-555.9 844-480.07q0 74.84-28.42 141.21t-78.21 116.18q-49.79 49.81-115.63 78.25Q555.9-116 480.07-116Z" />
      </svg>
    ),
    bgColor: "tw-bg-[#32d583]",
  },
  "not available": {
    key: "not available",
    icon: "warning",
    sideIcon: (
      <svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="20px" fill="#f04438">
        <path d="M82.62-164 480-824l397.38 660H82.62Zm397.17-107.69q11.94 0 20.23-8.08 8.29-8.08 8.29-20.02t-8.08-20.23q-8.08-8.29-20.02-8.29t-20.23 8.08q-8.29 8.08-8.29 20.02t8.08 20.23q8.08 8.29 20.02 8.29ZM454-376.31h52v-192h-52v192Z" />
      </svg>
    ),
    bgColor: "tw-bg-[#f04438]",
  },
};

const sortEnum = [
  { columnKey: "req_date", value: "req_date" },
  { columnKey: "delivered_date", value: "delivered_date" },
];
const RequestsDueDilligence = ({ index, alertDataSource }) => {
  const [loader, setLoader] = useState(true);
  const [currentpage, setCurrentpage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchedData, setSearchedData] = useState([]);
  const debounceInput = useDebounce(searchTerm, 300);
  const [sortValue, setSortValue] = useState(sortEnum[0].value);
  const [sortOrderValue, setSortOrderValue] = useState("desc"); // Default to descending
  const [data, setData] = useState([]);
  const [showStatusFilter, setShowStatusFilter] = useState(false);
  const [sFilterDataCount, setSFilterDataCount] = useState(0);
  const [statusFilterData, setStatusFilterData] = useState(alertDataSource);
  const [activeFilters, setActiveFilters] = useState([]);
  const { search: searchUrlParams, pathname } = useLocation();
  const dataSource = new URLSearchParams(searchUrlParams).get("dataSource");

  useEffect(() => {
    applyAllFilters(debounceInput, activeFilters);
  }, [debounceInput, activeFilters]);

  const applyAllFilters = (search, statusFilters) => {
    let filteredData = [...data];

    if (search && search.trim()) {
      filteredData = filteredData.filter((row) => {
        const requestIdMatch = row.report_details.request_id?.toLowerCase().includes(search.toLowerCase());
        const companyNameMatch = row.report_details.company_name?.toLowerCase().includes(search.toLowerCase());
        const userNameMatch = row.requestor_details.user_name?.toLowerCase().includes(search.toLowerCase());
        const userOrganisationMatch = row.requestor_details.organisation_name?.toLowerCase().includes(search.toLowerCase());

        return requestIdMatch || companyNameMatch || userNameMatch || userOrganisationMatch;
      });
    }

    // Apply status filters if any are active
    if (statusFilters.length > 0) {
      filteredData = filteredData.filter((item) => statusFilters.includes(item.status));
    }

    // Apply sorting
    if (sortValue && sortOrderValue) {
      if (sortValue === "req_date") {
        filteredData.sort((a, b) => {
          const dateA = new Date(a.report_details.req_date);
          const dateB = new Date(b.report_details.req_date);

          if (sortOrderValue === "asc") {
            return dateA - dateB;
          } else {
            return dateB - dateA;
          }
        });
      } else if (sortValue === "delivered_date") {
        filteredData.sort((a, b) => {
          // Handle cases where one or both might be "-"
          if (a.status !== statuses["delivered"].key && b.status !== statuses["delivered"].key) {
            return 0;
          }
          if (a.status !== statuses["delivered"].key) {
            return sortOrderValue === "asc" ? 1 : -1;
          }
          if (b.status !== statuses["delivered"].key) {
            return sortOrderValue === "asc" ? -1 : 1;
          }

          const dateA = new Date(a.report_details.delivered_date);
          const dateB = new Date(b.report_details.delivered_date);

          if (sortOrderValue === "asc") {
            return dateA - dateB;
          } else {
            return dateB - dateA;
          }
        });
      }
    }

    // Reset serial numbers
    filteredData = filteredData.map((item, index) => {
      return { ...item, s_no: index + 1, id: index + 1 };
    });

    setSearchedData(filteredData);
    // Reset to first page when filters change
    setCurrentpage(1);
  };

  const handleSorting = (column, sortDirection) => {
    setSortValue(column);
    setSortOrderValue(sortDirection);

    applyAllFilters(debounceInput, activeFilters);
  };

  const filterByStatus = (index) => {
    let statusDataClone = JSON.parse(JSON.stringify(statusFilterData));
    let newActiveFilters = [...activeFilters];
    const statusName = statusDataClone[index].name.toLowerCase();

    if (statusDataClone[index].isChecked) {
      statusDataClone[index].isChecked = false;
      setSFilterDataCount(sFilterDataCount - 1);
      newActiveFilters = newActiveFilters.filter((filter) => filter !== statusName);
    } else {
      statusDataClone[index].isChecked = true;
      setSFilterDataCount(sFilterDataCount + 1);
      newActiveFilters.push(statusName);
    }

    setStatusFilterData(statusDataClone);
    setActiveFilters(newActiveFilters);
    applyAllFilters(debounceInput, newActiveFilters);
  };

  const clearStatusFilter = () => {
    const resetFilters = statusFilterData.map((filter) => ({
      ...filter,
      isChecked: false,
    }));

    setStatusFilterData(resetFilters);
    setSFilterDataCount(0);
    setActiveFilters([]);
    applyAllFilters(debounceInput, []);
  };

  const renderStatus = (status) => {
    return (
      <div className="tw-flex tw-items-center tw-gap-1">
        <span>{getTitleCase(status)}</span>
        <span className={`material-symbols-outlined`}>{statuses[status].sideIcon}</span>
      </div>
    );
  };

  const companyRedirection = (row) => {
    let companySlug = row.report_details.slug;
    let aliasCompanyId = row.report_details.company_id;
    let url = "";
    if (companySlug && aliasCompanyId) {
      url = "/companies/" + companySlug + "/" + aliasCompanyId;
    }

    window.open(url, "_blank");
    return url;
  };

  const Columns = useMemo(() => {
    let column = [
      { key: "s_no", name: "S.No", selector: "s_no", width: "40px" },
      {
        name: "Request Id",
        wrap: true,
        maxWidth: "130px !important",
        minWidth: "130px !important",
        selector: (row) => <div>{row.report_details.request_id}</div>,
      },
      {
        name: "Company Name",
        wrap: true,
        grow: 4,
        maxWidth: "230px !important",
        minWidth: "200px !important",
        selector: (row) =>
          row.report_details.slug ? (
            <span className="tw-underline tw-text-primary-purple-600 ">
              <a
                className="tw-cursor-pointer"
                onClick={(e) => {
                  e.preventDefault();
                  companyRedirection(row);
                }}
                href="#">
                {getTitleCase(row.report_details.company_name)}
              </a>
            </span>
          ) : (
            <span>{getTitleCase(row.report_details.company_name)}</span>
          ),
      },
      {
        name: "Request Date",
        wrap: true,
        sortable: true,
        grow: 8,
        key: "req_date",
        minWidth: "130px !important",
        selector: (row) => <div>{row.report_details.req_date}</div>,
      },
      {
        name: "Requested By",
        wrap: true,
        selector: (row) => <div>{row.requestor_details.user_name}</div>,
      },
      {
        name: "Delivery Date",
        wrap: true,
        sortable: true,
        minWidth: "130px !important",
        key: "delivered_date",
        selector: (row) => <div className="">{row.status === statuses["delivered"].key ? row.report_details.delivered_date : "-"}</div>,
      },
      {
        name: (
          <span
            onClick={(e) => {
              e.stopPropagation();
            }}
            className="cursor-pointer">
            <span
              onClick={(e) => {
                e.stopPropagation();
                setShowStatusFilter(!showStatusFilter);
              }}>
              Status <img src="https://res.cloudinary.com/drzta9shk/image/upload/v1683033788/intel/icons/filter_alt_bhbmxj.svg" />
            </span>
            {sFilterDataCount > 0 && (
              <span className="green-badge">
                <span>{sFilterDataCount}</span>
                <span onClick={() => clearStatusFilter()} className="pl-1">
                  X
                </span>
              </span>
            )}
            <span>{showStatusFilter && <RequestCenterFilter isDownload={true} onChangeHandler={filterByStatus} data={statusFilterData} />}</span>
          </span>
        ),
        wrap: true,
        minWidth: "150px !important",
        selector: (row) => (
          <div className="tw-flex tw-flex-col tw-gap-1">
            {renderStatus(row.status)}
            {statuses["pending"].key === row.status && <span className="tw-text-xs tw-font-normal">{`Expected on ${row.report_details.est_delivery_date}`}</span>}
          </div>
        ),
      },
      {
        name: "Action",
        wrap: true,
        minWidth: "110px !important",
        selector: (row) => (
          <div>
            {statuses["delivered"].key === row.status ? (
              <span
                className="material-symbols-outlined tw-w-5 tw-cursor-pointer tw-text-gray-900"
                onClick={() => {
                  fileOpener(row, "pdf");
                }}>
                cloud_download
              </span>
            ) : (
              "-"
            )}
          </div>
        ),
      },
    ];

    return column;
  }, [currentpage, perPage, showStatusFilter, statusFilterData, searchedData, debounceInput, sortOrderValue, sortValue]);

  const getAllRequests = () => {
    setLoader(true);
    Axios.get(getApiUrl("getOrganisationDueDeligenceReports"), {})
      .then((res) => {
        if (res && res.status === 200) {
          let dataWithSerialNos = Array.isArray(res.data)
            ? res.data.map((item, index) => {
                return { ...item, s_no: index + 1, id: index + 1 };
              })
            : [];
          setData(dataWithSerialNos);
          setSearchedData(dataWithSerialNos);

          // Initial sort based on default sortValue and sortOrderValue
          if (sortValue && sortOrderValue) {
            handleSorting(sortValue, sortOrderValue);
          }
        }
      })
      .catch((err) => {
        captureException(err);
        toast.error("Something went wrong. Please try again later!!!");
      })
      .finally(() => {
        setLoader(false);
      });
  };

  const handlePageChange = (page, rowsPerPage) => {
    setCurrentpage(page);
    setPerPage(rowsPerPage);
  };

  const fileOpener = (row, fileType) => {
    let requestId = row.report_details.request_id;
    let companyId = row.report_details.company_id;
    trademoTrackingEvents(trackingEventDictionary.requestCenterDueDiligenceReportDownload, generateTrackingObject(dataSource, pathname, getUserActivePlan()));
    Axios.post(getApiUrl("downloadDueDeligenceReport"), {
      company_id: companyId,
      request_id: requestId,
      type: fileType,
    })
      .then((res) => {
        if (res && res.data && res.status === 200) {
          window.open(res.data.file_url, "_blank");
        }
      })
      .catch((err) => {
        captureException(err);
        toast.error("Something went wrong. Please try again later.");
      })
      .finally(() => {
        setLoader(false);
      });
  };

  const handleSearch = (searchValue) => {
    setSearchTerm(searchValue);
  };

  useEffect(() => {
    if (index === 3) {
      trademoTrackingEvents(trackingEventDictionary.requestCenterDueDiligenceReportTab, generateTrackingObject(dataSource, pathname, getUserActivePlan()));
      getAllRequests();
    }
  }, [index]);

  return (
    <div className={`${loader || data?.length === 0 ? "tw-h-[calc(100vh-96px)] tw-flex tw-items-center tw-justify-center" : "tw-py-6 tw-px-7 tw-flex tw-flex-col tw-gap-2"}`}>
      {loader ? (
        <img src={LoaderImg} alt="description of gif" />
      ) : data?.length ? (
        <>
          <div className="tw-w-[405px] tw-relative">
            <Search size={14} color="#101828" className="tw-absolute tw-top-3 tw-left-1.5" />
            <input type="text" className="tw-w-full tw-py-2 tw-border tw-border-gray-300 tw-rounded-lg tw-pl-7 tw-bg-gray-50" placeholder={`Search by request id, company name and requested by`} value={searchTerm} onChange={(e) => handleSearch(e.target.value)} />
          </div>

          <div className="tw-border tw-border-[#e7e7e7] tw-rounded-2xl tw-overflow-hidden">
            <ReactTableTrademo
              columns={Columns}
              data={searchedData.length > 0 || debounceInput.trim() || activeFilters.length > 0 ? searchedData : data}
              paginationTotalRows={searchedData.length > 0 || debounceInput.trim() || activeFilters.length > 0 ? searchedData.length : data.length}
              onSelectedRowsChange={() => null}
              onSort={handleSorting}
              defaultSortField={sortValue}
              defaultSortAsc={sortOrderValue === "desc" ? false : true}
              paginationPerPage={perPage}
              paginationDefaultPage={currentpage}
              selectedData={[]}
              sortOrderValue={sortOrderValue}
              shipmentDefaultSortValue={sortEnum}
              onChangeRowsPerPage={(perPage) => handlePageChange(currentpage, perPage)}
              onTableObjectChange={(data) => {
                setPerPage(data.perPage);
                setCurrentpage(data.currentPage + 1);
              }}
              onChangePage={(page) => handlePageChange(page, perPage)}
              noDataComponent={<NoSearchDataComponent content="No requests for Due Dilligence Reports Found." />}
              hideSelection={true}
              clearFilter={() => setSearchTerm("")}
              isAtleastTwoColumnsSorted={true}
            />
          </div>
        </>
      ) : (
        <div className="tw-w-full tw-h-full tw-flex tw-items-center tw-justify-center">
          <div className="tw-flex tw-flex-col tw-max-w-[50%] tw-items-center tw-text-center tw-gap-1">
            <img src="https://res.cloudinary.com/drzta9shk/image/upload/v1684997941/intel/icons/emptystate_norequest_b1wzx6.svg" />
            <p>You have not raised any requests yet!</p>
            <p>
              This <strong>Request Centre</strong> will show information on all your raised requests in detail which you can view as per your customised preferences
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default RequestsDueDilligence;
